import { Suspense, useEffect, useRef } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ToolBrowser } from '@/components/layout/ToolBrowser';
import { Dock } from '@/components/layout/Dock';
import { TabBar } from '@/components/layout/TabBar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TooltipProvider } from '@/components/ui/tooltip';
import { usePrefs } from '@/store/prefs';
import { useToolNavigation } from '@/hooks/useToolNavigation';
import { getTool, TOOLS_BY_ID } from '@/registry/tools';
import { applyTheme } from '@/lib/theme';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export function App() {
  const hydrated = usePrefs((s) => s.hydrated);
  const hydrate = usePrefs((s) => s.hydrate);
  const theme = usePrefs((s) => s.theme);
  const dockPosition = usePrefs((s) => s.dockPosition);
  const tabs = usePrefs((s) => s.tabs);
  const activeTabId = usePrefs((s) => s.activeTabId);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (hydrated) applyTheme(theme);
  }, [theme, hydrated]);

  /**
   * Deep-link handler — `?tool=<id>` opens that tool once hydration is done.
   * Lives here (not in the web entry) so the same code path works for both
   * the web /try/ build and any future web embed. In the extension context
   * the side-panel URL never carries a query string, so this is a no-op.
   */
  const deepLinkApplied = useRef(false);
  const openTool = usePrefs((s) => s.openTool);
  const pushRecent = usePrefs((s) => s.pushRecent);
  useEffect(() => {
    if (!hydrated || deepLinkApplied.current) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const toolId = params.get('tool');
    if (!toolId) {
      deepLinkApplied.current = true;
      return;
    }
    if (TOOLS_BY_ID[toolId]) {
      openTool(toolId);
      pushRecent(toolId);
    }
    // Strip the param so a later reload doesn't re-deep-link the user away
    // from wherever they actually navigated.
    params.delete('tool');
    const qs = params.toString();
    const cleanUrl =
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState(null, '', cleanUrl);
    deepLinkApplied.current = true;
  }, [hydrated, openTool, pushRecent]);

  const nav = useToolNavigation();
  const showingBrowser = nav.mode === 'browse' || !activeTabId;

  return (
    <TooltipProvider delayDuration={250}>
      <div className="flex h-screen min-h-0 flex-col bg-background">
        <Header
          query={nav.query}
          onQueryChange={nav.setQuery}
          isBrowsing={nav.mode === 'browse'}
          onHome={nav.back}
          searchInputRef={nav.searchInputRef}
        />
        <TabBar />
        <div
          className={cn(
            'flex min-h-0 flex-1',
            dockPosition === 'bottom' ? 'flex-col' : 'flex-row',
          )}
        >
          <main className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
            {/* Browser pane — visible when no tab is focused. */}
            <div
              className={cn('absolute inset-0', showingBrowser ? 'block' : 'hidden')}
            >
              <ScrollArea className="h-full">
                <ToolBrowser
                  query={nav.query}
                  visualItems={nav.visualItems}
                  highlightIndex={nav.highlightIndex}
                  setHighlightIndex={nav.setHighlightIndex}
                  isHighlighted={nav.isHighlighted}
                  onPick={nav.openTool}
                />
              </ScrollArea>
            </div>

            {/* Open tabs stay mounted to preserve per-tool state. The active
             *  tab is shown; the rest are display:none. */}
            {tabs.map((tab) => {
              const tool = getTool(tab.toolId);
              if (!tool) return null;
              const Component = tool.component;
              const isActive = tab.id === activeTabId && !showingBrowser;
              return (
                <div
                  key={tab.id}
                  className={cn('absolute inset-0', isActive ? 'block' : 'hidden')}
                  aria-hidden={!isActive}
                >
                  <Suspense
                    fallback={
                      <div className="flex h-full items-center justify-center text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    }
                  >
                    <Component />
                  </Suspense>
                </div>
              );
            })}
          </main>
          {dockPosition === 'right' ? <Dock orientation="vertical" /> : null}
          {dockPosition === 'bottom' ? <Dock orientation="horizontal" /> : null}
        </div>
        <Footer />
      </div>
    </TooltipProvider>
  );
}
